/**
 * Internal (ChemMasters employee) email detection — the frontend twin of
 * `services/shared/email_utils.py` `INTERNAL_DOMAINS` / `is_internal_email`.
 *
 * Used at the customer-creation boundary: an employee's address must never
 * become a customer's email, or every email that employee forwards to the
 * orders inbox exact-matches that account as the buyer.
 */

export const INTERNAL_EMAIL_DOMAINS = ['chemmasters.net', 'chemmasters.com'] as const;

/** Extract the bare address from `"Name <addr>"` or a plain address. */
export function extractEmailAddress(value: string | null | undefined): string {
  if (!value) return '';
  const angle = value.match(/<([^>]+)>/);
  return (angle ? angle[1] : value).trim().toLowerCase();
}

/** True when the address is on a ChemMasters internal domain. */
export function isInternalEmail(value: string | null | undefined): boolean {
  const addr = extractEmailAddress(value);
  if (!addr) return false;
  return INTERNAL_EMAIL_DOMAINS.some((d) => addr.endsWith(`@${d}`));
}

/**
 * Every internal address inside a free-text customer email field
 * (`"a@x.com; b@chemmasters.net"`), mirroring `find_internal_addresses`.
 */
export function findInternalAddresses(field: string | null | undefined): string[] {
  if (!field) return [];
  return field
    .split(/[;,\s]+/)
    .map(extractEmailAddress)
    .filter((addr) => addr && isInternalEmail(addr));
}
